import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { TextField } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import type { TemplateAttachment } from "../../../../models/TemplateAttachment";
import { AttachmentsDownloadButtons } from "../download-buttons/attachment-download-buttons.columns";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

interface Props {
	templateAttachment: TemplateAttachment[];
	onSelectionChanged: (selectedAttachment: TemplateAttachment[]) => void;
}

export const AssetAttachmentTable = ({
	templateAttachment,
	onSelectionChanged,
}: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const {
		state: { assetAttachments },
	} = useTemplatesContext();

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const extractFileName = (path: string) => {
		if (!path) return "";
		const match = path.match(/[^\/\\]+$/);
		return match ? match[0] : "";
	};

	const sortRows = () => {
		gridApi?.applyColumnState({
			state: [{ colId: "fileName", sort: "asc", sortIndex: 0 }],
		});
	};

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);

	const rows = useMemo(() => {
		const uniqueAttachments = new Map();
		assetAttachments.forEach((attachment: any) => {
			const key = attachment.UploadFileId.toString();
			if (!uniqueAttachments.has(key)) {
				uniqueAttachments.set(key, {
					...attachment,
					id: key,
					isSelected: templateAttachment.some(
						(wa) =>
							wa.UploadFileId === attachment.UploadFileId && wa.IsAssetResource,
					),
					assets: [
						{
							name: attachment.AssetName,
							id: attachment.AssetId,
							path: attachment.FullPath,
						},
					],
					fileName: extractFileName(attachment.Path || attachment.FullPath),
				});
			} else {
				const existingAttachment = uniqueAttachments.get(key);
				existingAttachment.assets.push({
					name: attachment.AssetName,
					id: attachment.AssetId,
					path: attachment.FullPath,
				});
			}
		});
		return Array.from(uniqueAttachments.values());
	}, [assetAttachments, templateAttachment]);

	const columns: ColDef[] = [
		{
			headerName: "",
			flex: 1,
			maxWidth: 50,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			sortIndex: 1,
			cellStyle: { justifyContent: "flex-start" },
		},
		{
			field: "fileName",
			headerName: "File Name",
			flex: 9,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.fileName?.localeCompare(nodeB.data.fileName);
				}
			},
		},
		{
			headerName: "",
			cellRenderer: AttachmentsDownloadButtons,
			field: "Download Attachment",
			flex: 2,
			minWidth: 140,
		},
	];

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows();
		const selectedRows = event.api.getSelectedRows();
		const selectedAssetAttachments = selectedRows.map((attachment) => ({
			...attachment,
			IsAssetResource: true,
		}));
		onSelectionChanged(selectedAssetAttachments);
	};

	const onFirstDataRendered = useCallback(() => {
		gridApi?.forEachNode((node) => {
			if (node.data.isSelected) {
				node.setSelected(true);
			}
		});
		sortRows();
	}, [gridApi, templateAttachment]);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	const onGridReady = (event: GridReadyEvent) => {
		setGridApi(event.api);
	};

	return (
		<>
			<Grid2
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid2>
			</Grid2>
			<div style={{ width: "100%", height: "45vh" }}>
				<AgGridReact
					getRowId={(params) => params.data.id}
					rowHeight={30}
					headerHeight={25}
					animateRows={true}
					onGridReady={onGridReady}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={globalDefaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					loadingOverlayComponent={loadingOverlayComponent}
					rowSelection="multiple"
					rowMultiSelectWithClick
					onSelectionChanged={onAgGridSelectionChanged}
					suppressContextMenu={true}
					loading={isLoading}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
